import { Box } from '@mui/material';
import * as React from 'react';
import { ListChildComponentProps, VariableSizeList } from 'react-window';

import { IEntityAutocompleteOption } from './DesktopEntityAutoCompleteSearch';
import { EntitySwitcherAutoCompleteRenderOption } from './EntitySwitcherAutoCompleteRenderOption';

export const ENTITY_POPPER_ID = 'entity-autocomplete-search-popper';

// react-window custom support types
interface IListChildData {
  entityOption: IEntityAutocompleteOption;
  muiComponentProps: IEntityOptionMuiProps;
}

export interface IListItemMUIProps {
  'aria-disabled': boolean;
  'aria-selected': boolean;
  className: string;
  'data-option-index': number;
  id: string;
  role: string;
  tabIndex: number;
  onClick(e: React.MouseEvent): void;
  onMouseMove(e: React.MouseEvent): void;
  onTouchStart(e: React.TouchEvent): void;
}

/**
 * What Autocomplete hands `renderOption` for a single option. Every field is optional
 * because MUI's own published type promises less than it passes, and `key` is only
 * present on v6.
 */
export type IEntityOptionMuiProps = Partial<IListItemMUIProps> & { key?: React.Key };

export interface IEntityOptionElementProps {
  // List item props passed in by MUI Autocomplete
  muiProps: IEntityOptionMuiProps;
  // Entity item data
  option: IEntityAutocompleteOption;
}

/**
 * Autocomplete supplies the listbox's own attributes (`aria-labelledby`, `className`,
 * `id`, `role`, `onMouseDown`) and they are forwarded verbatim to the outer element, so
 * this is `HTMLAttributes` rather than a hand-written list of them. Declaring them as
 * required is what previously made this component unassignable to MUI's
 * `ListboxComponent` type and forced an `as unknown as` cast at the call site.
 */
type IListBoxComponentBaseProps = React.HTMLAttributes<HTMLElement>;

export type ReactWindowContextValueType = {
  displayEntityNamesOnly: boolean;
  handleEntityOptionClick(entity: IEntityAutocompleteOption): void;
};

// This react-window implementation with MUI Autocomplete is based off of
// MUI's doc example here: https://mui.com/material-ui/react-autocomplete/#virtualization
// Context to pass down the entity option click handler and displayEntityNamesOnly prop
// These cannot be passed directly to the ListboxComponent, so we use a context to pass them down instead.
export const ReactWindowContext = React.createContext<ReactWindowContextValueType>({
  displayEntityNamesOnly: false,
  handleEntityOptionClick: () => undefined
});

/**
 * A single option row, shared by both render paths below.
 *
 * `muiProps` carries a `key` in MUI v6; React treats a spread `key` as the element
 * key and warns about it, so it is pulled out here rather than forwarded.
 */
const EntityOptionRow: React.FC<IEntityOptionElementProps & { styleOverrides?: React.CSSProperties }> = ({
  muiProps,
  option,
  styleOverrides
}) => {
  const { handleEntityOptionClick, displayEntityNamesOnly } = React.useContext(ReactWindowContext);
  const listItemProps: IEntityOptionMuiProps = { ...muiProps };
  delete listItemProps.key;

  return (
    <EntitySwitcherAutoCompleteRenderOption
      {...listItemProps}
      styleOverrides={styleOverrides}
      displayEntityNamesOnly={displayEntityNamesOnly}
      onItemClick={() => handleEntityOptionClick(option)}
      entity={option}
    />
  );
};

/**
 * What `renderOption` returns for each option.
 *
 * `ReactWindowListboxComponentBase` never renders these: it reads `muiProps`/`option`
 * back off each child and draws only the visible rows through react-window. They are
 * real elements rather than the raw `[props, option, index]` tuples this used to pass
 * so that a host whose Autocomplete does not apply our listbox degrades to a plain
 * unvirtualized row. Returning a bare props object there asks React to render an
 * object and throws once per option — with a large entitlement that is thousands of
 * synchronous throws, which locks up the tab (CRD-13449).
 */
export const EntityOptionElement: React.FC<IEntityOptionElementProps> = ({ muiProps, option }) => (
  <EntityOptionRow muiProps={muiProps} option={option} />
);

// MUI Autocomplete controls its search dropdown via a ref to the listbox component.
// This custom outer element is used as the list box base for react-window
// so that MUI can control the dropdown via the ref while react-window handles the rendering.
const OuterElementContext = React.createContext<any>({});
const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

const renderReactWindowRow = ({ index, style, data }: ListChildComponentProps<IListChildData[]>) => {
  // 'data' is the array of all entity options passed in by react-window.
  // 'index' is the index of the current entity option being rendered.
  // We get the entity for each row by accessing the entityOption property of the data array
  // at the current index.
  const { entityOption, muiComponentProps } = data[index];
  const reactWindowStyles = {
    ...style,
    top: index !== 0 ? style.top : 8
  };

  return (
    <EntityOptionRow
      key={entityOption.subdomain}
      muiProps={muiComponentProps}
      option={entityOption}
      styleOverrides={reactWindowStyles}
    />
  );
};

const useResetCache = (data: any) => {
  const ref = React.useRef<VariableSizeList<IListChildData[]>>(null);
  React.useEffect(() => {
    if (ref.current !== null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
};

// Adapter for react-window to work with MUI Autocomplete
const ReactWindowListboxComponentBase: React.ForwardRefRenderFunction<HTMLDivElement, IListBoxComponentBaseProps> = (
  { children, ...componentProps },
  ref
) => {
  const entityListItemDataArray: IListChildData[] = [];
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement<IEntityOptionElementProps>(child)) {
      return;
    }
    entityListItemDataArray.push({
      entityOption: child.props.option,
      muiComponentProps: child.props.muiProps
    });
  });
  const itemCount = entityListItemDataArray.length;

  // As auto-complete options are filtered, the listbox will be re-rendered
  const gridRef = useResetCache(itemCount);

  return (
    <Box ref={ref}>
      <OuterElementContext.Provider value={componentProps}>
        <VariableSizeList
          itemData={entityListItemDataArray}
          height={250}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType as any}
          innerElementType="ul"
          itemSize={() => 48}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderReactWindowRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </Box>
  );
};
export const ReactWindowListBoxComponent = React.forwardRef<HTMLDivElement, IListBoxComponentBaseProps>(
  ReactWindowListboxComponentBase
);
